import { useAuth0 } from '@auth0/auth0-react';
import { useContext, useState, useEffect } from 'react';

// imports relevant for HTTP calls
import DisplayException from '../../error_messages/DisplayException';
import CircleLoader from 'react-spinners/CircleLoader';
import { SERVER_URL } from '../../config_server_url';

// ----- Imports for main window --------
import SimilarityRangeTableViewer from './SimilarityRangeTableViewer'
import DisplayCandidatesViewer from './DisplayCandidatesViewer';
import DisplayCycleMeasurement from './DisplayCycleMeasurement';
import UserSessionContext from '../../user_session/context_user_session';
import CycleSetup from './CycleSetup';
import {exportCandiatesTableXLS,
        exportColumns2XLS} from './utils_xls_export';


function CyclePlannerViewer(props) {

    const usr_session_ctx = useContext(UserSessionContext);
    const { user } = useAuth0();
    
    // ------------------ Display HTTP/Server Exceptions ---------------------------------
    const [Exception, setException] = useState(false);
    const [ExceptionMessage, setExceptionMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    
    // ------------------ Main window -----------------------------------------
    const [cycle, setCycle] = useState({});
    const [factors, setFactors] = useState([]);
    const [subscores, setSubscores] = useState([]);
    const [excludeSimilar, setExcludeSimilar] = useState(false);
    
    const [cycleMeasurement, setCycleMeasurement] = useState([]);
    const [rawDataFiles, setRawDataFiles] = useState([]);
    const [experimentalProcedure, setExperimentalProcedure] = useState('')
    const [candidates2Rows, setCandidates2Rows] = useState([]);
    

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true); // Set loading state to true when fetch begins
            const data = {
                uuid_opt_campaign: props.uuid_opt_campaign,
                cycle_id: props.cycle_id,
            };
    
            try {
                // Prepare the fetch requests
                const request1 = fetch(`${SERVER_URL}/current_opt_campaign/load_optimization_cycle/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(data),
                });
    
                const request2 = fetch(`${SERVER_URL}/current_opt_campaign/load_factors_active_design_space/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(props.uuid_opt_campaign),
                });
    
                const request3 = fetch(`${SERVER_URL}/current_opt_campaign/load_subscores_active_objective/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(props.uuid_opt_campaign),
                });
              
                const request4 = fetch(`${SERVER_URL}/current_opt_campaign/load_cycle_measurement/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'uuid_tenant': usr_session_ctx.uuid_tenant
                    },
                    body: JSON.stringify(data),
                });


                // Wait for both requests to complete
                const [response1, response2, response3, response4] = await Promise.all([
                    request1,
                    request2,
                    request3,
                    request4
                ]);
    
                // Check responses
                if (!response1.ok) {
                    throw new Error(`HTTP error ${response1.status}`);
                }
                if (!response2.ok) {
                    throw new Error(`HTTP error ${response2.status}`);
                }
                if (!response3.ok) {
                    throw new Error(`HTTP error ${response3.status}`);
                }
                if (!response4.ok) {
                    throw new Error(`HTTP error ${response4.status}`);
                }
                // Parse JSON from responses
                const loaded_cycle = await response1.json();
                const loaded_factors = await response2.json();
                const loaded_subscores = await response3.json();
                const loaded_cycle_measurement = await response4.json();

                // Update state with loaded data
                setCycle(loaded_cycle.data);
                setFactors(loaded_factors.data);
                setSubscores(loaded_subscores.data);
                // Initialize excludeSimilar
                setExcludeSimilar(loaded_cycle.data.similarity_range.length > 0);
    
                // Initialize candidate table
                initialize_candidate_table(loaded_factors.data, loaded_cycle.data.selected_candidates);

                // set uploaded cycle measurement data
                setCycleMeasurement(loaded_cycle_measurement.data.data);
                setRawDataFiles(loaded_cycle_measurement.data.raw_data);
                setExperimentalProcedure(loaded_cycle_measurement.data.experimental_procedure);
                // Set loading state to false
                setIsLoading(false);
            } catch (err) {
                setIsLoading(false);
                setException(true);
                setExceptionMessage(`HTTPException: err_msg=${err.message}`);
            }
        };
        if(props.cycle_id >=0){
            loadData();
        }
    }, [props.cycle_id, props.uuid_opt_campaign]);

    // If candidates exist
    function initialize_candidate_table(in_factors, in_candiates){ 
            // let col_names = [...props.factorNames];
            // col_names.unshift('');
            // setColNames(col_names);
            let factor_names= in_factors.map((factor) => { 
                        if(factor.units !== '') { 
                            return `${factor.name} [${factor.units}]`;
                        }
                        else{
                            return `${factor.name}`;
                        }
                    });
            let candidates = in_candiates.map( (candidate) => {
                    let row = { };
                    factor_names.forEach((name, id) => {
                    row = Object.assign(row, { [name]: candidate.factors[id] });
                    });
                    row = Object.assign(row, { 'strategy': candidate.strategy });
                    return row;
                });
            setCandidates2Rows(candidates);
    };


    
    async function handleDisplayPrecision(event){
        let parser = event.target.id.split('_');
        let factor_id = parseInt(parser[1]);
        console.log('factor_id', factor_id)
        let updatedCycle = {...cycle};
        if(parser[0]==='increase') {
            updatedCycle.displayed_candidate_precision[factor_id] +=1;
        }
        if(parser[0]==='reduce') {
            updatedCycle.displayed_candidate_precision[factor_id] -=1;
            if (updatedCycle.displayed_candidate_precision[factor_id]<1){
                updatedCycle.displayed_candidate_precision[factor_id] = 1;
            }
        }
        setCycle(updatedCycle);

        try{
            let cycle_data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                               uuid_opt_campaign: props.uuid_opt_campaign, 
                               cycle_id: props.cycle_id,
                               cycle: cycle}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/update_opt_cycle_inactive/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(cycle_data)
            });
            if(response.ok){
                await response.json();
                setCycle(updatedCycle);
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }

    }


    async function handleDisplayPrecisionCycleMeasurement(event){
        let parser = event.target.id.split('_');
        let col_id = parseInt(parser[1]);
        let updatedCycle = {...cycle};
        if(parser[0]==='increase') {
            updatedCycle.displayed_cycle_measurement_precision[col_id] +=1;
        }
        if(parser[0]==='reduce') {
            updatedCycle.displayed_cycle_measurement_precision[col_id] -=1;
            if (updatedCycle.displayed_cycle_measurement_precision[col_id]<1){
                updatedCycle.displayed_cycle_measurement_precision[col_id] = 1;
            }
        }
        setCycle(updatedCycle);

        try{
            let cycle_data = { uuid_project: usr_session_ctx.session_info.uuid_current_project,
                               uuid_opt_campaign: props.uuid_opt_campaign, 
                               cycle_id: props.cycle_id,
                               cycle: cycle}
            let headers = {
                'Content-Type': 'application/json',
                'auth0_user': user.sub,
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/update_opt_cycle_inactive/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(cycle_data)
            });
            if(response.ok){
                await response.json();
                setCycle(updatedCycle);
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setIsLoading(false);
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }

    }


    async function handleDownloadRawDataFile(event){
        let parser = event.target.id.split('_');
        let raw_data_id = parseInt(parser[1]);
        try{
            
            let headers = {
                'Content-Type': 'application/json',
                'uuid_tenant': usr_session_ctx.uuid_tenant
            };
            const response = await fetch(`${SERVER_URL}/current_opt_campaign/download_raw_data_file/`, {
                            method: 'POST',
                            headers: headers,
                            body: JSON.stringify(rawDataFiles[raw_data_id].uuid_raw_data_file)
            });
            if(response.ok){
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', rawDataFiles[raw_data_id].filename); // Change this if you want a different name for the downloaded file
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            }
            if(!response.ok){
                throw new Error(`HTTP error ${response.status}`);
            }
        } catch(err) {
            setException(true);
            setExceptionMessage(`HTTPException: err_msg=${err.message}`);
        }
    }


    function handleExportTableXLS() {
        const filename = `candidates_cycle_${props.cycle_id+1}.xlsx`;
        exportCandiatesTableXLS(factors, cycle, filename);
    }



    function handleExportCycleMeasurementTableXLS(){
        const filename = `cycle_measurement_${props.cycle_id+1}.xlsx`;
        exportColumns2XLS(cycleMeasurement[0].csv_data.columns, filename);
    }


    let active =(<></>);

    if(cycle.active){
        active = (<>
                <div style={{display:'flex', alignItems:'center'}}>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'8px', height:'10px', width:'10px', borderRadius:'50%', background:'var(--aixelo-green)'}}></div>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'0px'}}> active cycle</div>
                </div>
        </>);
    }
    if(!cycle.active){
        active = (<>
                <div style={{display:'flex', alignItems:'center'}}>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'8px', height:'10px', width:'10px', borderRadius:'50%', background:'var(--aixelo-red)'}}></div>
                    <div style={{marginTop:'0px', marginBottom:'0px', marginRight:'0px'}}> cycle inactive</div>
                </div>
        </>);
    }

    let similarity_range = (<></>);
    if(excludeSimilar){
        similarity_range = (<>
            <div style = {{fontSize:'14px', alignItems:'top', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%', overflow:'auto'}}>
                <div style={{fontWeight:'bold', marginTop:'10px', marginBottom:'10px',}}>Similarity ranges:</div>
                <SimilarityRangeTableViewer 
                        similarity_range={cycle.similarity_range}
                        factors={factors} />
                <div style={{display: 'flex', marginTop:'10px', flexDirection: 'row', alignItems:'center'}}>
                    <div style={{width: '300px', minWidth: '300px', marginRight:'15px'}}>Two candiates are considered similar if the difference in all factors is smaller than the 
                        <span style={{fontWeight:'bold', color:'black'}}> similarity range.</span>
                    </div>
                    <div style={{display:'flex', width:'40px', minWidth:'60px', minHeight:'40px', height: '40px', background: 'var(--aixelo-gray-light)', alignItems:'center', justifyContent:'center'}}>
                        <div style={{width:'32px', height: '27px', background: 'black'}}>
                            <div style={{marginLeft:'7px', marginTop:'7px', width:'6px', height: '6px', borderRadius:'50%', background: 'white'}}> </div>
                            <div style={{marginLeft:'16px', marginTop:'3px', width:'6px', height: '6px', borderRadius:'50%', background: 'white'}}> </div>
                        </div>
                    </div>                         
                </div>
            </div>
        </>);
    }  


    let planner = (<>
        <div style={{display:'flex', alignItems:'center', width:'95%'}}>
            <h4 style = {{margin:'0px', marginTop:'10px', marginLeft:'10px', marginBottom:'5px', width:'fit-content'}} >
                Planner Settings 
            </h4>
            <div style={{flexGrow: '1'}}></div>
            {active}
        </div>
        <div style = {{display:'flex', fontSize:'14px', alignItems:'center', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%'}}>
            <div style={{width:'80px'}}>Strategy:</div>
            <div style={{ fontSize: '14px' }}> 
                {cycle.strategy}
            </div>
        </div>
        <div style = {{display:'flex', fontSize:'14px', alignItems:'center', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%'}}>
            <div style={{width:'80px'}}>Batch Size:</div>
            <div style={{ fontSize: '14px' }}> 
               {cycle.batch_size}
            </div>
        </div>
        <div style = {{display:'flex', marginTop:'15px', fontSize:'14px', alignItems:'center', margin:'0px', marginLeft:'10px', marginBottom:'5px', height:'fit-content', width:'95%'}}>
            <div style={{width:'20px', marginRight:'3px', display:'flex'}}>
                <input 
                        id={`is_similarity_${props.cycle_id}`} 
                        style={{margin:'0px', height:'14px'}}
                        type='checkbox' 
                        checked={excludeSimilar}
                    /> 
            </div>
            <div style={{marginTop:'0px'}}>Exclude Similar Candiates</div>
        </div>
        {similarity_range}
    </>);


    let display_candidates = (<>
        <DisplayCandidatesViewer 
            width={props.width}
            candidates={candidates2Rows}
            factorNames={factors.map((factor) => { 
                                if(factor.units !== '') { 
                                    return `${factor.name} [${factor.units}]`;
                                }
                                else{
                                    return `${factor.name}`;
                                }
                            })}
            displayPrecision={cycle.displayed_candidate_precision}
            // modifiedCustomDesign={modifiedCustomDesign}
            handleDisplayPrecision={handleDisplayPrecision}
            // onSelectCandidates={handleSelectCandidate}
            // handleCustomDesignInput={handleCustomDesignInput}
            // handleCustomDesign={handleCustomDesign}
            // handleCustomDesignSave={handleCustomDesignSave}
            // handleCustomDesignDiscard={handleCustomDesignDiscard}
            // handleDelete={handleDeleteCandidates}
            // handleReplace={handleReplaceCandidates}
            handleExportTableXLS={handleExportTableXLS}
           />        
    </>);

    let show_uploaded_candiates = (<>
        <DisplayCycleMeasurement 
                        displayPrecision={cycle.displayed_cycle_measurement_precision}
                        width={props.width}
                        cycleMeasurement={cycleMeasurement}
                        rawDataFiles={rawDataFiles}
                        experimentalProcedure={experimentalProcedure}
                        handleDisplayPrecision={handleDisplayPrecisionCycleMeasurement}
                        downloadFile={handleDownloadRawDataFile}
                        handleExportTableXLS={handleExportCycleMeasurementTableXLS} />
        </>);




    if(isLoading && !Exception){
        return (<div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'90%'}} >
                    <CircleLoader loading={isLoading} size={40} color='var(--aixelo-green)' />
            </div>);
    }

    if(Exception){
        return (<>
                <div style={{display:'flex', justifyContent:'center', alignItems:'center',height:'90%'}} >
                    <DisplayException>
                        {ExceptionMessage}
                    </DisplayException>  
                </div>
            </>);
    }



    return (<>
        <CycleSetup width={props.width} 
                    cycle_id={props.cycle_id}
                    uuid_opt_campaign={props.uuid_opt_campaign}
                    onView={props.onView}
                    onEdit={props.onEdit} />
        <div style = {{width: `${props.width * 0.91 - 20}px`, marginLeft: '20px', background: 'white', marginTop: '20px', marginBottom: '20px', border:'0.5px solid var(--aixelo-gray-light)'}}>
            {planner}
        </div>
        {display_candidates}
        {show_uploaded_candiates}
    </>);

}


export default CyclePlannerViewer